import { styled, alpha } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Skeleton from '@mui/material/Skeleton'

const StateContainer = styled(Box)(() => ({
  textAlign: 'center',
  minHeight: 120,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
}))

const LoadingItemContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  display: 'flex',
  alignItems: 'flex-start',
  gap: '12px',
}))

// Empty State Component
export const EmptyState = () => (
  <StateContainer data-testid="notification-empty-state">
    <Box
      sx={{
        maxWidth: 200,
        mt: 8.5,
      }}
    >
      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ mb: 1, fontWeight: 700 }}
      >
        You&apos;re all caught up
      </Typography>
    </Box>
  </StateContainer>
)

// Loading State Component
export const LoadingState = () => (
  <Box data-testid="notification-loading-state">
    {Array.from({ length: 3 }, (_, index) => (
      <LoadingItemContainer key={index}>
        <Skeleton variant="circular" width={40} height={40} />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width="80%" height={20} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width="100%" height={16} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width="60%" height={16} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="40%" height={14} />
        </Box>
      </LoadingItemContainer>
    ))}
  </Box>
)

// Error State Component
interface ErrorStateProps {
  error: string
  onRetry?: () => void
}

export const ErrorState = ({ error, onRetry }: ErrorStateProps) => (
  <StateContainer data-testid="notification-error-state">
    <Typography
      variant="h6"
      component="h3"
      sx={{ mb: 1, fontWeight: 500, color: 'error.main' }}
    >
      Unable to load notifications
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      {error}
    </Typography>
    {onRetry && (
      <Button
        variant="outlined"
        size="small"
        onClick={onRetry}
        data-testid="retry-button"
      >
        Try again
      </Button>
    )}
  </StateContainer>
)

// Connection Status Indicator (for real-time connection)
interface ConnectionStatusProps {
  connected: boolean
}

export const ConnectionStatus = ({ connected }: ConnectionStatusProps) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.5,
      px: 2,
      py: 0.5,
      backgroundColor: theme =>
        connected
          ? alpha(theme.palette.success.main, 0.1)
          : alpha(theme.palette.warning.main, 0.1),
      borderBottom: 1,
      borderColor: 'divider',
    }}
    data-testid="connection-status"
  >
    <Box
      sx={{
        width: 6,
        height: 6,
        borderRadius: '50%',
        backgroundColor: connected ? 'success.main' : 'warning.main',
      }}
    />
    <Typography variant="caption" color="text.secondary">
      {connected ? 'Live' : 'Reconnecting...'}
    </Typography>
  </Box>
)
